import { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import Input from '../../../shared/Input';
import Divider from '../../../shared/Divider';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import { PartnerClient } from '../../../../types';
import { useDebounce } from '../../../../hooks/useDebounce';
import { isBlankString, isNotUndefined } from '../../../../utils/utils';
import { useTranslate } from '../../../../hooks/useTranslate';
import Text from '../../../shared/Typography';
import Spinner from '../../../shared/Spinner';
import useQuery from '../../../../hooks/useQuery';

type UserDropDownItemProps = {
  user: PartnerClient;
  handleClick: (user: PartnerClient) => any;
  query: string;
  isActive: boolean;
  scrollIntoView: boolean;
  block: ScrollLogicalPosition;
};

const UserDropDownItem = ({ user, handleClick, query, isActive, scrollIntoView, block }: UserDropDownItemProps) => {
  const getUserName = `${user.first_name} ${user.last_name}`;

  const handleSelection = (user: PartnerClient) => {
    handleClick(user);
  };

  const handleHighlightText = (name: string, highlight: string) => {
    // Check if name is null or undefined and handle accordingly
    if (name === null || name === undefined) {
      return null;
    }

    const formattedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = name.split(new RegExp(`(${formattedHighlight})`, 'gi'));

    return (
      <span>
        {parts.map((part: string, i: number) => (
          <span key={`${part}-${i}`} style={part.toLowerCase() === highlight.toLowerCase() ? { color: '#5B53FF' } : {}}>
            {part}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div
      className={clsx('dropdown-item selectable', isActive && 'selected')}
      onClick={() => {
        handleSelection(user);
      }}
      ref={(ref) => {
        if (scrollIntoView && ref) ref.scrollIntoView({ block });
      }}
    >
      <div className={clsx('dropdown-item')}>
        {handleHighlightText(getUserName, query)}
        <div className={clsx('dropdown-subitem')}>{handleHighlightText(user.email, query)}</div>
      </div>
    </div>
  );
};

export interface ClientSelectProps {
  onSelect: (user: PartnerClient) => void;
  addNewProviderButtonText?: string;
  hidden?: boolean;
  placeholder?: string;
}

const ClientSelect = (props: ClientSelectProps) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState<{ id: number; index: number } | undefined>();
  const [clients, setClients] = useState<PartnerClient[]>([]);
  const debounceQuery = useDebounce(
    useMemo(() => (isBlankString(query) ? '' : encodeURIComponent(query)), [query]),
    200
  );
  const { loading: isUsersLoading, makeRequest } = useQuery(
    `/users?include=insurance,address&search=${debounceQuery}`,
    'GET'
  );
  useEffect(() => {
    const searchClients = async () => {
      const response = await makeRequest();
      const { users: clientsFound } = response;

      setClients(clientsFound);
    };

    if (isBlankString(debounceQuery)) {
      setClients([]);
    } else {
      searchClients();
    }
    // eslint-disable-next-line
  }, [debounceQuery]);
  const { t } = useTranslate('clientSelect');

  const { ref } = useOutsideClick<HTMLDivElement>(() => {
    setQuery('');
    setActive(undefined);
  });

  const isActive = (clientId: number, index: number) => {
    return !!(active && active.id === clientId && active.index === index);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const setUser = (user: PartnerClient) => {
    props.onSelect(user);
    setQuery('');
  };

  const next = () => {
    if (!active?.id && !active?.index) {
      const firstClient = clients[0];
      return setActive({ id: firstClient.id, index: 0 });
    }

    const newAddressIndex = active.index! + 1;
    const newClient = clients[newAddressIndex];
    if (newClient) {
      return setActive({ id: newClient.id, index: newAddressIndex });
    }
  };

  const prev = () => {
    if (!active?.index && !active?.id) {
      const lastClientIndex = clients.length - 1;
      const lastClient = clients[lastClientIndex];
      if (lastClient) {
        setActive({ id: lastClient.id, index: lastClientIndex });
      }
    } else {
      const previousClientIndex = active.index! - 1;
      if (previousClientIndex >= 0) {
        const prevClient = clients[previousClientIndex];
        if (prevClient) {
          setActive({ id: prevClient.id, index: previousClientIndex });
        }
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    if (!clients.length) {
      return;
    }
    if (key === 'ArrowUp') {
      prev();
      e.preventDefault();
    } else if (key === 'ArrowDown') {
      next();
      e.preventDefault();
    } else if (key === 'Enter') {
      if (active && isNotUndefined(active.id) && isNotUndefined(active.index)) {
        setUser(clients[active.index]);
      }
    } else {
      setActive(undefined);
    }
  };

  if (props.hidden) return null;

  return (
    <div ref={ref}>
      <div className='dropdown'>
        <Input
          maxWidth
          focusOnMount
          autoComplete='off'
          required
          iconName='search'
          value={query}
          onChange={onSearch}
          onKeyDown={onKeyDown}
          placeholder={props.placeholder || 'Search by name or email address'}
          className='is-rounded center'
          name='Institution'
          style={{ padding: '13px 28px' }}
        />
        {!isBlankString(debounceQuery) && (
          <div className='dropdown-menu'>
            <div className='dropdown-content'>
              <div className='dropdown-content-container'>
                {isUsersLoading ? (
                  <Text paragraph style={{ padding: '21px 36px' }}>
                    <Spinner size='small' />
                  </Text>
                ) : (
                  <>
                    {clients.length > 0 ? (
                      clients.map((client, index) => {
                        return (
                          <div key={`${client.email}-${client.id}-${index}`}>
                            <UserDropDownItem
                              scrollIntoView={index === active?.index || (!active?.id && index === 0)}
                              user={client}
                              handleClick={setUser}
                              query={query}
                              isActive={isActive(client.id, index)}
                              block={active?.index === index && index + 1 === clients.length ? 'start' : 'nearest'}
                            />
                            <Divider />
                          </div>
                        );
                      })
                    ) : (
                      <Text paragraph color='grey-60' style={{ padding: '21px 36px' }}>
                        {t('errors.noPatientsFound', { searchQuery: debounceQuery })}
                      </Text>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSelect;
